import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Input, Select } from "../../components/ui/Input";
import { Switch } from "../../components/ui/Switch";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import type { CampaignType, ContentItem } from "../../lib/types";
import { contrast, isHexColor } from "./color";
import { ColorField } from "./ColorField";
import { TickerPreview } from "./TickerPreview";
import {
	TICKER_PRESETS,
	tickerColors,
	type TickerAppearance,
	type TickerColors,
} from "./tickerAppearance";
import { useCreateContent, useUpdateContent } from "./useContent";
import { useTiers } from "./useTiers";

const PREVIEW_PROGRESS = 0.55;

/**
 * A code campaign's ticker points at an offer to unlock rather than a gift to
 * claim, and once unlocked it shows the code instead of a progress bar.
 */
const COPY = {
	gift: {
		headline: "Free gift event",
		description: "A small corner badge that counts down and points at the next gift.",
		progressHint: "Hidden automatically once a gift is unlocked and waiting to be claimed.",
		noEndDate: "shows the next gift and progress",
	},
	code: {
		headline: "Limited-time offer",
		description:
			"A small corner badge that counts down and shows progress toward unlocking your code.",
		progressHint: "Replaced by the code once the shopper unlocks it.",
		noEndDate: "shows the offer and progress",
	},
} as const;

const copyFor = (type: CampaignType) => (type === "threshold_code" ? COPY.code : COPY.gift);

const hexOr = (value: unknown, fallback: string): string =>
	typeof value === "string" && isHexColor(value) ? value : fallback;

const readTheme = (value: unknown): TickerAppearance["theme"] =>
	value === "dark" || value === "custom" ? value : "light";

/** The ticker's copy is 12–13px, so it needs the WCAG AA ratio for small text. */
const MIN_CONTRAST = 4.5;

const contrastRatio = ({ background, text }: TickerColors): number => contrast(background, text);

const readAppearance = (item: ContentItem | null, headline: string): TickerAppearance => {
	if (!item) {
		return {
			headline,
			theme: "light",
			...TICKER_PRESETS.light,
			position: "bottom-right",
			showProgress: true,
		};
	}
	const settings = item.settings ?? {};
	return {
		headline: item.title || headline,
		theme: readTheme(settings.theme),
		background: hexOr(settings.background, TICKER_PRESETS.light.background),
		text: hexOr(settings.text, TICKER_PRESETS.light.text),
		position: settings.position === "bottom-left" ? "bottom-left" : "bottom-right",
		showProgress: settings.showProgress !== false,
	};
};

const pad = (value: number): string => String(value).padStart(2, "0");

const previewCountdown = (endsAt: string | null): string | null => {
	if (!endsAt) return null;
	const remaining = Date.parse(endsAt) - Date.now();
	if (!Number.isFinite(remaining) || remaining <= 0) return null;

	const total = Math.floor(remaining / 1000);
	const days = Math.floor(total / 86400);
	const hours = pad(Math.floor((total % 86400) / 3600));
	const minutes = pad(Math.floor((total % 3600) / 60));
	const seconds = pad(total % 60);

	return days > 0 ? `${days}d ${hours}:${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
};

type TickerFormProps = {
	campaignId: string;
	campaignType: CampaignType;
	campaignEndsAt: string | null;
	item: ContentItem | null;
	open: boolean;
	onClose: () => void;
};

export const TickerForm = ({
	campaignId,
	campaignType,
	campaignEndsAt,
	item,
	open,
	onClose,
}: TickerFormProps) => {
	const create = useCreateContent(campaignId, "floating_ticker");
	const update = useUpdateContent(campaignId, "floating_ticker");
	const tiers = useTiers(campaignId);
	const toast = useToast();
	const copy = copyFor(campaignType);
	const isCode = campaignType === "threshold_code";

	const [appearance, setAppearance] = useState<TickerAppearance>(() =>
		readAppearance(null, copy.headline),
	);

	useEffect(() => {
		if (!open) return;
		setAppearance(readAppearance(item, copy.headline));
	}, [open, item, copy.headline]);

	// A representative mid-campaign cart, so the preview shows a real name and a
	// plausible gap rather than an empty shell.
	const firstTier = tiers.data?.items[0];
	const previewTitle = isCode
		? (firstTier?.code?.offerLabel ?? "Your offer")
		: (firstTier?.gifts[0]?.title ?? "Your first gift");
	const remainingMinor = Math.round((firstTier?.thresholdMinor ?? 5000) * (1 - PREVIEW_PROGRESS));

	const saving = create.isPending || update.isPending;
	const set = (patch: Partial<TickerAppearance>) => setAppearance({ ...appearance, ...patch });

	const isCustom = appearance.theme === "custom";
	const colorsValid =
		!isCustom || (isHexColor(appearance.background) && isHexColor(appearance.text));
	const lowContrast = colorsValid && contrastRatio(tickerColors(appearance)) < MIN_CONTRAST;

	// Starting a custom scheme from the preset on screen means the merchant
	// adjusts one colour instead of picking two from scratch.
	const chooseTheme = (theme: TickerAppearance["theme"]) =>
		set(theme === "custom" && !isCustom ? { theme, ...tickerColors(appearance) } : { theme });

	const submit = () => {
		const payload = {
			enabled: true,
			title: appearance.headline.trim() || copy.headline,
			settings: {
				theme: appearance.theme,
				...(isCustom ? { background: appearance.background, text: appearance.text } : {}),
				position: appearance.position,
				showProgress: appearance.showProgress,
			},
			altText: null,
			desktopAssetId: null,
			mobileAssetId: null,
			startsAt: null,
			endsAt: null,
		};
		const onError = (error: unknown) => toast.push((error as Error).message, "error");
		const onSuccess = () => {
			toast.push("Floating ticker saved");
			onClose();
		};

		if (item) {
			update.mutate({ itemId: item.id, ...payload }, { onSuccess, onError });
			return;
		}
		create.mutate(payload, { onSuccess, onError });
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => !next && onClose()}
			title={item ? "Edit floating ticker" : "Add floating ticker"}
			description={copy.description}
			footer={
				<>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={saving || !colorsValid}>
						{saving ? "Saving…" : "Save ticker"}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				<TickerPreview
					appearance={{
						...appearance,
						...(colorsValid ? {} : TICKER_PRESETS.light),
						headline: appearance.headline || copy.headline,
					}}
					title={previewTitle}
					withImage={!isCode}
					remainingMinor={remainingMinor}
					progress={PREVIEW_PROGRESS}
					countdown={previewCountdown(campaignEndsAt)}
				/>

				<label className="block space-y-1.5">
					<LabelWithHint label="Headline">
						The small line above the countdown. A few words only. The badge is deliberately compact.
					</LabelWithHint>
					<Input
						autoFocus
						value={appearance.headline}
						maxLength={40}
						placeholder={copy.headline}
						onChange={(event) => set({ headline: event.target.value })}
					/>
				</label>

				<div className="grid gap-4 sm:grid-cols-2">
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Theme</span>
						<Select
							value={appearance.theme}
							onChange={(event) => chooseTheme(readTheme(event.target.value))}
						>
							<option value="light">Light</option>
							<option value="dark">Dark</option>
							<option value="custom">Custom</option>
						</Select>
					</label>
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Corner</span>
						<Select
							value={appearance.position}
							onChange={(event) =>
								set({ position: event.target.value as TickerAppearance["position"] })
							}
						>
							<option value="bottom-right">Bottom right</option>
							<option value="bottom-left">Bottom left</option>
						</Select>
					</label>
				</div>

				{isCustom ? (
					<div className="space-y-2">
						<div className="grid gap-4 sm:grid-cols-2">
							<ColorField
								label="Background"
								value={appearance.background}
								onChange={(background) => set({ background })}
							/>
							<ColorField label="Text" value={appearance.text} onChange={(text) => set({ text })} />
						</div>
						{lowContrast ? (
							<p className="text-xs text-ink-muted">
								These colours are close together, so the countdown and offer may be hard to read on
								a phone. The progress bar uses the text colour too.
							</p>
						) : null}
					</div>
				) : null}

				<div className="border-t border-line pt-4">
					<Switch
						checked={appearance.showProgress}
						onChange={(event) => set({ showProgress: event.target.checked })}
						label="Show the progress bar"
						hint={copy.progressHint}
					/>
				</div>

				{!campaignEndsAt ? (
					<p className="rounded-md border border-line-strong bg-raised px-3 py-2 text-xs text-ink-muted">
						This campaign has no end date, so the ticker {copy.noEndDate} without a countdown. Set
						an end date on the Schedule step to add one.
					</p>
				) : null}
			</div>
		</Dialog>
	);
};
